import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LifeBuoy, Mail, BookOpen, MessageSquare, FileText } from 'lucide-react';
import SEO from '@/components/seo/SEO';

const SupportPage: React.FC = () => {
  const supportEmail = "danielgaskins99@gmail.com";

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO title="RunOffTrack Support & Inspection Resources" description="Get help with RunOffTrack, browse inspection resources, find account and billing answers, or contact support." canonical="/support" />
      
      {/* Hero */}
      <div className="py-20 md:py-28 text-center container px-4">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">How can we help?</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Find answers in our documentation or get in touch with our support team.
        </p>
      </div>

      {/* Resources Grid */}
      <div className="container px-4 pb-24">
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          
          {/* Card 1 */}
          <Card className="bg-card border-border/50 hover:border-primary/30 hover:shadow-lg transition-all group cursor-pointer h-full">
            <CardHeader>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <BookOpen size={24} />
              </div>
              <CardTitle>Field Resources</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm leading-relaxed">
                Browse inspection templates and field references for your next project.
              </p>
              <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 hover:bg-transparent" asChild>
                <Link to="/resources/templates">Browse Templates &rarr;</Link>
              </Button>
            </CardContent>
          </Card>

          {/* Card 2 */}
          <Card className="bg-card border-border/50 hover:border-primary/30 hover:shadow-lg transition-all group cursor-pointer h-full">
            <CardHeader>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <MessageSquare size={24} />
              </div>
              <CardTitle>Community FAQ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm leading-relaxed">
                Quick answers to the most common questions about billing, account management, and more.
              </p>
              <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 hover:bg-transparent" asChild>
                <Link to="/faq">View FAQs &rarr;</Link>
              </Button>
            </CardContent>
          </Card>

          {/* Card 3 */}
          <Card className="bg-card border-border/50 hover:border-primary/30 hover:shadow-lg transition-all group cursor-pointer h-full">
            <CardHeader>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Mail size={24} />
              </div>
              <CardTitle>Email Support</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-sm leading-relaxed">
                Describe your question and the workflow you need help with.
              </p>
              <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 hover:bg-transparent" asChild>
                 <a href={`mailto:${supportEmail}`}>Contact Us &rarr;</a>
              </Button>
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-muted/30 py-16 border-t border-border">
        <div className="container px-4 text-center">
          <p className="text-sm text-muted-foreground">
            Want to try the workflow? Open the <Link to="/demo/sample-inspection" className="text-primary underline">sample inspection</Link> without creating an account.
          </p>
        </div>
      </div>

    </div>
  );
};

export default SupportPage;
